import React, { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { X, Loader2, AlertCircle } from "lucide-react";
import axios from "axios";

import { config } from "../../utils/config";
import ParticipantPayment from "../payments/ParticipantPayment";
import TeamBadge from "../../components/TeamBadge/TeamBadge";
import StatisticsTab from "../../components/TeamStats/StatisticsTab";
import PlayersTab from "../../components/TeamStats/PlayersTab";
import clubbieLogo from "../../assets/images/clubbie_logo_full.svg";

const mapApiStats = (api) => {
  if (!api) return null;
  const goalDiff = (s, c) => { const d = (s || 0) - (c || 0); return d >= 0 ? `+${d}` : `${d}`; };
  return {
    matches:     api.games || 0,
    goals:       api.goalsScored || 0,
    yellowCards: api.yellowCards || 0,
    cleanSheets: api.cleanSheets || 0,
    assists:     api.assists || 0,
    redCards:    api.redCards || 0,
    averageGPG: {
      total: api.avgGoals || api.avgGoalsPerMatch || 0,
      home:  api.avgGoalsHome || api.avgGoalsPerMatchHome || 0,
      away:  api.avgGoalsAway || api.avgGoalsPerMatchAway || 0,
    },
    winLossRecord: {
      total:        api.games || 0,
      totalMatches: api.games || 0,
      win:  { count: api.wins   || 0, percentage: Math.round(api.winPercentage  || 0) },
      draw: { count: api.draws  || 0, percentage: Math.round(api.drawPercentage || api.drawnPercentage || 0) },
      loss: { count: api.losses || 0, percentage: Math.round(api.lossPercentage || api.lostPercentage  || 0) },
    },
    latestMatchForm: [
      { venue: "Overall", results: (api.lmResults     || []).map(r => (r||"").toUpperCase()), gpg: api.lmGPG     ?? api.avgGoals     ?? 0, gd: goalDiff(api.goalsScored    ?? api.scored,    api.goalsConceded    ?? api.conceded)    },
      { venue: "Home",    results: (api.lmResultsHome || []).map(r => (r||"").toUpperCase()), gpg: api.lmGPGHome ?? api.avgGoalsHome ?? 0, gd: goalDiff(api.goalsScoredHome ?? api.scoredHome, api.goalsConcededHome ?? api.concededHome) },
      { venue: "Away",    results: (api.lmResultsAway || []).map(r => (r||"").toUpperCase()), gpg: api.lmGPGAway ?? api.avgGoalsAway ?? 0, gd: goalDiff(api.goalsScoredAway ?? api.scoredAway, api.goalsConcededAway ?? api.concededAway) },
    ],
    keyStatistics: [
      { gameResult: "Overall", wins: api.wins    || 0, avgGPG: api.avgGoals     || 0, scored: api.scored     || api.goalsScored    || 0, conceded: api.conceded     || api.goalsConceded    || 0, cleanSheets: api.cleanSheets     || 0 },
      { gameResult: "Home",    wins: api.wonHome || 0, avgGPG: api.avgGoalsHome || 0, scored: api.scoredHome || api.goalsScoredHome || 0, conceded: api.concededHome || api.goalsConcededHome || 0, cleanSheets: api.cleanSheetsHome || 0 },
      { gameResult: "Away",    wins: api.wonAway || 0, avgGPG: api.avgGoalsAway || 0, scored: api.scoredAway || api.goalsScoredAway || 0, conceded: api.concededAway || api.goalsConcededAway || 0, cleanSheets: api.cleanSheetsAway || 0 },
    ],
  };
};

const mapApiTopPlayers = (topStats) => {
  if (!topStats) return [];
  const players = [];
  if (topStats.topAttendance?.player) players.push({ name: topStats.topAttendance.player.fullName || "Unknown", stat: "Most appearances", value: topStats.topAttendance.count || 0 });
  if (topStats.topGoals?.player)      players.push({ name: topStats.topGoals.player.fullName      || "Unknown", stat: "Most goals",       value: topStats.topGoals.count      || 0 });
  if (topStats.topAssists?.player)    players.push({ name: topStats.topAssists.player.fullName    || "Unknown", stat: "Most Assists",     value: topStats.topAssists.count    || 0 });
  return players;
};

/**
 * Team Dashboard Page — shown after invitation acceptance
 * Uses shared StatisticsTab + PlayersTab components (same as TeamOverview)
 */
const TeamDashboard = ({ isAdminView = false }) => {
  const { teamId } = useParams();
  const [searchParams] = useSearchParams();

  const token      = searchParams.get("token") || localStorage.getItem(`team_${teamId}_token`) || "";
  const tournamentId = searchParams.get("tournamentId") || "";
  const categoryId   = searchParams.get("categoryId") || searchParams.get("divisionId") || "";
  const mode         = searchParams.get("mode") || "";
  const isReadOnly   = isAdminView || mode === "admin";

  const [activeTab, setActiveTab]     = useState("statistics");
  const [teamInfo, setTeamInfo]       = useState({
    name:  searchParams.get("teamName") || "Team",
    owner: searchParams.get("ownerName") || "",
    crest: null,
  });

  const [invitationStatus, setInvitationStatus] = useState(isReadOnly ? "accepted" : null);
  const [checkingStatus,   setCheckingStatus]   = useState(!isReadOnly);
  const [invitationError,  setInvitationError]  = useState(null);

  const [players,         setPlayers]         = useState([]);
  const [stats,           setStats]           = useState(null);
  const [topPlayers,      setTopPlayers]      = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState(null);
  const [searchQuery,     setSearchQuery]     = useState("");
  const [selectedPlayers, setSelectedPlayers] = useState([]);

  const [showAddModal,    setShowAddModal]    = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [playerToDelete,  setPlayerToDelete]  = useState(null);
  const [actionLoading,   setActionLoading]   = useState(false);

  // Persist token
  useEffect(() => {
    if (token && teamId) localStorage.setItem(`team_${teamId}_token`, token);
  }, [token, teamId]);

  // Check invitation status
  const checkInvitationStatus = useCallback(async () => {
    if (isReadOnly) { setCheckingStatus(false); return; }
    try {
      setCheckingStatus(true);
      const storedToken = localStorage.getItem(`team_${teamId}_token`) || token;
      const response = await axios.get(
        `${config.API_BASE_URL}/${config.API_PATH}/teams/${teamId}/invitation-status`,
        { params: { token: storedToken, tournamentId, categoryId } }
      );
      const raw = response.data.status?.toLowerCase();
      const normalized =
        raw === "accept" || raw === "accepted" ? "accepted" :
        raw === "reject" || raw === "rejected" ? "rejected" :
        raw || null;
      setInvitationStatus(normalized);
      if (response.data.teamName) setTeamInfo((p) => ({ ...p, name: response.data.teamName }));
    } catch (err) {
      setInvitationError(err.response?.data?.message || "Failed to verify invitation");
      setInvitationStatus("error");
    } finally {
      setCheckingStatus(false);
    }
  }, [teamId, token, tournamentId, categoryId, isReadOnly]);

  useEffect(() => {
    if (teamId && !isReadOnly) checkInvitationStatus();
  }, [teamId, checkInvitationStatus, isReadOnly]);

  // Fetch players
  const fetchPlayers = useCallback(async () => {
    if (invitationStatus !== "accepted") return;
    try {
      setLoading(true);
      const storedToken = localStorage.getItem(`team_${teamId}_token`) || token;
      const response = await axios.get(
        `${config.API_BASE_URL}/${config.API_PATH}/tournaments/${tournamentId}/categories/${categoryId}/teams/${teamId}/roster/players`,
        { params: { token: storedToken } }
      );
      setPlayers(response.data.players || []);
      if (response.data.teamName) setTeamInfo((p) => ({ ...p, name: response.data.teamName }));
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load players");
    } finally {
      setLoading(false);
    }
  }, [teamId, token, tournamentId, categoryId, invitationStatus]);

  useEffect(() => {
    if (teamId && invitationStatus === "accepted") fetchPlayers();
  }, [teamId, fetchPlayers, invitationStatus]);

  // Fetch team stats
  useEffect(() => {
    if (invitationStatus !== "accepted" || !tournamentId || !categoryId || !teamId) return;
    const fetchStats = async () => {
      try {
        const storedToken = localStorage.getItem(`team_${teamId}_token`) || token;
        const response = await axios.get(
          `${config.API_BASE_URL}/${config.API_PATH}/tournaments/${tournamentId}/categories/${categoryId}/teams/${teamId}/stats`,
          { params: { token: storedToken } }
        );
        const mapped = mapApiStats(response.data);
        if (mapped) setStats(mapped);
        const mappedTop = mapApiTopPlayers(response.data?.topStats);
        if (mappedTop.length > 0) setTopPlayers(mappedTop);
      } catch (err) {
      }
    };
    fetchStats();
  }, [teamId, token, tournamentId, categoryId, invitationStatus]);

  const handleAddPlayer = async (playerData) => {
    try {
      setActionLoading(true);
      const storedToken = localStorage.getItem(`team_${teamId}_token`) || token;
      const response = await axios.post(
        `${config.API_BASE_URL}/${config.API_PATH}/tournaments/${tournamentId}/categories/${categoryId}/teams/${teamId}/roster/players`,
        { ...playerData, token: storedToken }
      );
      setPlayers((p) => [...p, response.data.player]);
      setShowAddModal(false);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.message || "Failed to add player" };
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeletePlayer = async (playerId) => {
    try {
      setActionLoading(true);
      const storedToken = localStorage.getItem(`team_${teamId}_token`) || token;
      await axios.delete(
        `${config.API_BASE_URL}/${config.API_PATH}/tournaments/${tournamentId}/categories/${categoryId}/teams/${teamId}/roster/players/${playerId}`,
        { data: { token: storedToken } }
      );
      setPlayers((p) => p.filter((pl) => pl._id !== playerId));
      setShowDeleteModal(false);
      setPlayerToDelete(null);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete player");
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedPlayers.length) return;
    try {
      setActionLoading(true);
      const storedToken = localStorage.getItem(`team_${teamId}_token`) || token;
      await axios.post(
        `${config.API_BASE_URL}/${config.API_PATH}/tournaments/${tournamentId}/categories/${categoryId}/teams/${teamId}/roster/players/bulk-delete`,
        { token: storedToken, playerIds: selectedPlayers }
      );
      setPlayers((p) => p.filter((pl) => !selectedPlayers.includes(pl._id)));
      setSelectedPlayers([]);
      setShowDeleteModal(false);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete players");
    } finally {
      setActionLoading(false);
    }
  };

  const invitationLink = `/invitation/${token}?tournamentId=${tournamentId}&categoryId=${categoryId}&teamId=${teamId}`;

  /* ── Loading state ──────────────────────────────────────────────────────── */
  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-app-bg flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#00E7FF] animate-spin mx-auto mb-4" />
          <p className="text-app-text-secondary">Verifying your access...</p>
        </div>
      </div>
    );
  }

  /* ── Not-accepted state ─────────────────────────────────────────────────── */
  if (invitationStatus !== "accepted") {
    return (
      <div className="min-h-screen bg-app-bg flex items-center justify-center p-4">
        <div className="bg-app-surface border border-app-border rounded-2xl py-6 px-8 max-w-md w-full text-center">
          <div className="flex items-center justify-center mb-5">
            <img className="h-auto w-[140px]" src={clubbieLogo} alt="Clubbie" />
          </div>
          <div className="w-14 h-14 bg-[#FFD700]/20 border border-[#FFD700]/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-7 h-7 text-[#FFD700]" />
          </div>
          <h1 className="text-[18px] font-semibold text-app-text mb-2">
            {invitationStatus === "pending"  ? "Invitation Not Yet Accepted" :
             invitationStatus === "rejected" ? "Invitation Declined" :
             invitationStatus === "error"    ? "Access Error" :
                                              "Invitation Not Found"}
          </h1>
          <p className="text-app-text-secondary text-sm mb-6">
            {invitationStatus === "pending"  ? "Accept the tournament invitation to access your team dashboard." :
             invitationStatus === "rejected" ? "This invitation has been declined." :
             invitationStatus === "error"    ? (invitationError || "There was an error verifying your access.") :
                                              "We couldn't find an invitation for this team. Please check the link and try again."}
          </p>
          {invitationStatus === "pending" && (
            <a
              href={invitationLink}
              className="inline-block px-6 py-3 bg-[#00E7FF] text-[#021529] rounded-full font-medium hover:bg-[#00cce6] transition-all text-sm"
            >
              Accept Invitation
            </a>
          )}
          {invitationStatus === "error" && (
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-app-card text-app-text rounded-full font-medium hover:bg-app-card/80 transition-all text-sm"
            >
              Try Again
            </button>
          )}
        </div>
      </div>
    );
  }

  /* ── Main view ──────────────────────────────────────────────────────────── */
  return (
    <div className="min-h-screen bg-app-bg">
      {/* Header */}
      <header className="bg-[#000F1F] border-b border-[#0D1B29] px-6 py-4">
        <div className="flex items-center justify-between">
          <img className="h-auto w-[150px]" src={clubbieLogo} alt="Clubbie" />
        </div>
      </header>

      <main className="px-6 py-6">
        {/* Team Info */}
        <div className="flex items-center gap-3 mb-4">
          <TeamBadge src={teamInfo.crest} alt={teamInfo.name} containerSize="w-12 h-12" />
          <div>
            <h1 className="text-lg font-semibold text-app-text">{teamInfo.name}</h1>
            {teamInfo.owner && (
              <p className="text-sm text-app-text-secondary">Owner: {teamInfo.owner}</p>
            )}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex items-center gap-3 mb-6">
          <TabButton active={activeTab === "statistics"} onClick={() => setActiveTab("statistics")}>
            Statistics
          </TabButton>
          <TabButton active={activeTab === "players"} onClick={() => setActiveTab("players")}>
            Players
          </TabButton>
          <TabButton active={activeTab === "payment"} onClick={() => setActiveTab("payment")}>
            Payments
          </TabButton>
        </div>

        {/* Tab Content */}
        {activeTab === "statistics" && (
          <StatisticsTab
            stats={stats}
            teamName={teamInfo.name}
            hasData={!!stats}
            isReadOnly={isReadOnly}
          />
        )}
        {activeTab === "players" && (
          <PlayersTab
            players={players}
            topPlayers={topPlayers}
            loading={loading}
            error={error}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            isReadOnly={isReadOnly}
            selectedPlayers={selectedPlayers}
            setSelectedPlayers={setSelectedPlayers}
            onAddClick={() => setShowAddModal(true)}
            onDeleteClick={(player) => { setPlayerToDelete(player); setShowDeleteModal(true); }}
            onBulkDeleteClick={() => setShowDeleteModal(true)}
          />
        )}
        {activeTab === "payment" && (
          <ParticipantPayment embedded teamId={teamId} />
        )}
      </main>

      {/* Add Player Modal */}
      {showAddModal && (
        <AddPlayerModal
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddPlayer}
          loading={actionLoading}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <DeleteConfirmModal
          onClose={() => { setShowDeleteModal(false); setPlayerToDelete(null); }}
          onConfirm={playerToDelete ? () => handleDeletePlayer(playerToDelete._id) : handleBulkDelete}
          loading={actionLoading}
          isBulk={!playerToDelete}
          count={playerToDelete ? 1 : selectedPlayers.length}
          playerName={playerToDelete?.name}
        />
      )}
    </div>
  );
};

/* ─── Tab Button ─────────────────────────────────────────────────────────── */

const TabButton = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`h-[40px] px-4 rounded-full text-sm font-medium transition-all ${
      active
        ? "bg-[#001A2A] text-[#00E7FF]"
        : "text-app-text-secondary hover:text-[#00E7FF]"
    }`}
  >
    {children}
  </button>
);

/* ─── Add Player Modal ───────────────────────────────────────────────────── */

const AddPlayerModal = ({ onClose, onSubmit, loading }) => {
  const [name,         setName]         = useState("");
  const [email,        setEmail]        = useState("");
  const [jerseyNumber, setJerseyNumber] = useState("");
  const [error,        setError]        = useState("");

  const canSubmit = name.trim() && email.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const result = await onSubmit({ name, email, jerseyNumber });
    if (!result.success) setError(result.error);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-app-bg w-full max-w-md rounded-2xl border border-app-border shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 py-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 mb-4">
          <h2 className="text-[15px] font-medium text-[#FEFCEB]">Add player</h2>
          <button onClick={onClose} className="text-app-text-secondary hover:text-[#FEFCEB] transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div className="px-6 space-y-4 mb-4">
            {error && (
              <div className="bg-[#FF7B7B]/10 border border-[#FF7B7B]/30 rounded-xl p-3">
                <p className="text-[#FF7B7B] text-sm">{error}</p>
              </div>
            )}
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-[#FEFCEB]">Member name</label>
              <input
                type="text" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Enter member name here" autoFocus
                className="w-full bg-app-surface border border-app-surface rounded-3xl px-4 py-2.5 text-sm text-[#FEFCEB] placeholder-app-text-secondary focus:border-blue-shade-2 outline-none transition-colors"
              />
            </div>
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-[#FEFCEB]">Email</label>
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email here"
                className="w-full bg-app-surface border border-app-surface rounded-3xl px-4 py-2.5 text-sm text-[#FEFCEB] placeholder-app-text-secondary focus:border-blue-shade-2 outline-none transition-colors"
              />
            </div>
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-[#FEFCEB]">
                Kit number <span className="text-app-text-secondary font-normal">(optional)</span>
              </label>
              <input
                type="text" value={jerseyNumber} onChange={(e) => setJerseyNumber(e.target.value)}
                placeholder="Enter kit number"
                className="w-full bg-app-surface border border-app-surface rounded-3xl px-4 py-2.5 text-sm text-[#FEFCEB] placeholder-app-text-secondary focus:border-blue-shade-2 outline-none transition-colors"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 flex justify-end gap-3">
            <button
              type="button" onClick={onClose}
              className="px-6 py-3 text-sm font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit" disabled={!canSubmit || loading}
              className="bg-[#00E7FF] hover:bg-[#00cce6] text-[#021529] font-medium px-6 py-3 rounded-full text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Adding...</> : "Add"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ─── Delete Confirm Modal ───────────────────────────────────────────────── */

const DeleteConfirmModal = ({ onClose, onConfirm, loading, isBulk, count, playerName }) => (
  <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
    <div className="bg-app-bg w-full max-w-sm rounded-2xl border border-app-border shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 py-4">
      {/* Header */}
      <div className="flex items-center justify-between px-6 mb-4">
        <h2 className="text-[15px] font-medium text-[#FEFCEB]">
          {isBulk ? `Delete ${count} Players?` : "Delete Player?"}
        </h2>
        <button onClick={onClose} className="text-app-text-secondary hover:text-[#FEFCEB] transition-colors">
          <X size={20} />
        </button>
      </div>

      {/* Body */}
      <div className="px-6 mb-4">
        <p className="text-app-text-secondary text-sm">
          {isBulk
            ? `Are you sure you want to delete ${count} selected players? This action cannot be undone.`
            : `Are you sure you want to delete ${playerName}? This action cannot be undone.`}
        </p>
      </div>

      {/* Footer */}
      <div className="px-6 flex justify-end gap-3">
        <button
          onClick={onClose}
          className="px-6 py-3 text-sm font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm} disabled={loading}
          className="bg-[#FF7B7B] hover:bg-[#FF7B7B]/90 text-white font-medium px-6 py-3 rounded-full text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Deleting...</> : "Delete"}
        </button>
      </div>
    </div>
  </div>
);

export default TeamDashboard;
